import { lazy, Suspense } from "react";
import { useStockReport } from "../hooks/index.js";
import ReportHeader from "./report/ReportHeader.jsx";
import { Banner, LoadingState } from "./ui/Primitives.jsx";
import Modal from "./ui/Modal.jsx";
import ContentBoundary from "./ui/ContentBoundary.jsx";

const StockReport = lazy(() => import("./report/StockReport.jsx"));

export default function ReportOverlay({ symbol, holdingYears, onClose }) {
  const { report, error, loading } = useStockReport(symbol, holdingYears);

  return (
    <Modal label={`${symbol} report`} onClose={onClose} className="report-dialog">
      <div className="overlay-panel">
        <ReportHeader report={report} symbol={symbol} price={report?.price?.last} onClose={onClose} />

        <div className="overlay-body">
          {loading && <ReportSkeleton symbol={symbol} />}
          {error && (
            <Banner tone="vlow" title="Could not build the report.">{error}</Banner>
          )}
          {report && (
            <ContentBoundary key={`${symbol}:${holdingYears}`} title="This report view could not open.">
              <Suspense fallback={<LoadingState title="Opening the report">Loading its charts and research tabs.</LoadingState>}>
                <StockReport report={report} />
              </Suspense>
            </ContentBoundary>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Twelve sections are fetched in parallel and take a while; a bare spinner
// gives no sense of that, so this shows the shape that is coming.
function ReportSkeleton({ symbol }) {
  return (
    <div className="skeleton" aria-live="polite">
      <p className="skeleton-note">
        Building the report for <strong>{symbol}</strong> — pulling live figures and confirming each one
        against its source.
      </p>
      <div className="sk-block sk-verdict" />
      <div className="sk-block sk-chart" />
      <div className="sk-row">
        <div className="sk-block sk-tile" />
        <div className="sk-block sk-tile" />
        <div className="sk-block sk-tile" />
        <div className="sk-block sk-tile" />
      </div>
      <div className="sk-block sk-table" />
    </div>
  );
}
